import React from 'react';
import PropTypes from 'prop-types';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { analyticsChartTheme } from 'util/chartUtils';
import IntlMessages from '../../../../util/IntlMessages';
import DashboardPanel from './DashboardPanel';
import styles from './DashboardChart.module.scss';

const SearchEfficiency = ({ search }) => {
  const localizeMessage = useLocalizedMessage();
  const empty = !search || Boolean(search.error);
  const model = search || {};
  const data = empty ? [] : [
    { name: localizeMessage('dashboard.searchEfficiency.chart.noActions'), value: model.totalBad || 0 },
    { name: localizeMessage('dashboard.searchEfficiency.chart.refinedFilters'), value: model.usedFilters || 0 },
    { name: localizeMessage('dashboard.searchEfficiency.chart.navigatedFullSearch'), value: model.navigateViewAll || 0 },
    { name: localizeMessage('dashboard.searchEfficiency.chart.navigatedProductSearch'), value: model.navigateProductSearch || 0 },
    { name: localizeMessage('dashboard.searchEfficiency.chart.navigatedProductSearchbox'), value: model.navigateProductSuggest || 0 },
  ];

  return (
    <DashboardPanel
      title={<IntlMessages id="dashboard.search.efficiency.header" />}
      tone="searches"
      empty={empty}
      emptyVariant="bars"
    >
      <div className={styles.split}>
        <dl className={styles.metricList}>
          <div className={styles.metricRow}>
            <dt>{localizeMessage('dashboard.searchEfficiency.totalSessions', { total: model.total })}</dt>
            <dd>{model.total || 0}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{localizeMessage('dashboard.searchEfficiency.tookAction', { rate: model.totalGoodRateWithBounds })}</dt>
            <dd>{`${model.totalGoodRateWithBounds || 0}%`}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{localizeMessage('dashboard.searchEfficiency.immediatelyFound', { rate: model.navigateProductSuggestRate })}</dt>
            <dd>{`${model.navigateProductSuggestRate || 0}%`}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{localizeMessage('dashboard.searchEfficiency.requiredRefinement', { rate: model.navigateViewAllTotalRate })}</dt>
            <dd>{`${model.navigateViewAllTotalRate || 0}%`}</dd>
          </div>
        </dl>
        <div className={styles.chartFrame} role="img" aria-label={localizeMessage('dashboard.search.efficiency.header')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid stroke={analyticsChartTheme.grid} horizontal={false} strokeDasharray="2 7" />
              <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: analyticsChartTheme.muted, fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={150} axisLine={false} tickLine={false} tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }} />
              <Tooltip cursor={{ fill: analyticsChartTheme.searchesFill }} />
              <Bar dataKey="value" fill={analyticsChartTheme.searches} radius={[0, 5, 5, 0]} barSize={16} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardPanel>
  );
};

SearchEfficiency.propTypes = {
  search: PropTypes.shape({
    navigateProductSuggestRate: PropTypes.number,
    navigateViewAllTotalRate: PropTypes.number,
    total: PropTypes.number,
    totalGoodRateWithBounds: PropTypes.number,
    totalBad: PropTypes.number,
    usedFilters: PropTypes.number,
    navigateViewAll: PropTypes.number,
    navigateProductSearch: PropTypes.number,
    navigateProductSuggest: PropTypes.number,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
};

SearchEfficiency.defaultProps = { search: null };

export default SearchEfficiency;
